// MACHINERY V2 (Stroje) — mutual exclusion for v2 confirmation dialogs
// (MDEL-FE review finding 1).
//
// >>> THE BUG THIS EXISTS TO PREVENT IS A WRONG-MACHINE HARD DELETE. <<<
// ConfirmationProvider is an app-level SINGLETON (index.tsx:22-24) and its
// `handleShowDialog` (ConfirmationProvider.tsx:40-43) does
// `setConfirmationProps(props); setOpen(true)` UNCONDITIONALLY — it swaps the
// contents of an already-open dialog in place. So:
//   1. Admin picks Zmazať natrvalo on machine A. Eligibility fans out to three
//      peer services and takes seconds, during which the page looks idle.
//   2. The user opens machine B's kebab and picks Obnoviť. That is a different
//      code path, so useMachineDelete's own busyRef cannot see it. B's restore
//      dialog opens.
//   3. Eligibility resolves and calls confirm() for A. Title, body and
//      onConfirm are replaced under the user's cursor while the primary button
//      stays in the same screen position.
//   4. A click already in motion permanently deletes A instead of restoring B.
//
// THE PROVIDER IS DELIBERATELY NOT MODIFIED. It is shared with the live app, and
// the manager ruled it untouched this wave, so this module cannot ask it whether
// a dialog is open. Instead it keeps its OWN flag and requires every v2 confirm
// to open through here — which is achievable because v2 has exactly three
// openers (fleet/useFleetHandlers restore, profile/useProfileActions restore,
// fleet/useMachineDelete delete) and they all import this hook.
//
// WHAT THIS DOES NOT COVER, STATED PLAINLY: a confirmation opened by code that
// does NOT route through this hook — the live /machinery pages, any other
// module, or a future v2 opener that forgets — is invisible to the flag. Such a
// dialog could still be hijacked, and it would leave `openSince` set with no
// close callback of ours to clear it. In practice a MUI Dialog puts a backdrop
// over the page, so a second opener cannot be CLICKED while one is open; the
// realistic path is the asynchronous one above, which is fully covered. The
// staleness escape below is the backstop for the theoretical leak: without it a
// leaked flag would disable v2 delete for the rest of the session, which is the
// exact "bricked feature" failure mode this review pass is closing elsewhere.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback } from "react";

import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";

/** Structural copy of ConfirmationProvider's own (unexported) props type. */
export interface V2ConfirmProps {
  title: string;
  text: string;
  confirmButton?: string;
  closeButton?: string;
  severity?: "neutral" | "error";
  onConfirm?: () => void;
  onClose?: () => void;
}

// Module scope on purpose: the flag must be shared by every v2 surface and it
// must NOT drive rendering. Reading it inside a click handler is the only use.
let openSince = 0;

/** A dialog our own callbacks never got to close (see the coverage note) stops
 *  blocking after this. Far longer than any real hijack window, far shorter
 *  than a session. */
const STALE_AFTER_MS = 120000;

const isV2ConfirmOpen = (): boolean =>
  openSince > 0 && Date.now() - openSince < STALE_AFTER_MS;

/**
 * Opens a confirmation, unless one is already open.
 *
 * @returns `true` when the dialog was opened, `false` when the caller was
 *          refused. A `false` MUST be surfaced to the user — silently dropping
 *          it is what makes an unresponsive button.
 */
export const useV2Confirm = (): ((props: V2ConfirmProps) => boolean) => {
  const confirm = useConfirmation();

  return useCallback(
    (props: V2ConfirmProps): boolean => {
      if (isV2ConfirmOpen()) return false;
      openSince = Date.now();
      confirm({
        ...props,
        // Cleared BEFORE the caller's callback runs, so a handler is free to
        // open another confirmation from inside onConfirm.
        onConfirm: () => {
          openSince = 0;
          if (props.onConfirm) props.onConfirm();
        },
        // Covers Cancel, backdrop click and Escape — the provider funnels all
        // three into handleClose (ConfirmationProvider.tsx:52-57).
        onClose: () => {
          openSince = 0;
          if (props.onClose) props.onClose();
        },
      });
      return true;
    },
    [confirm]
  );
};

export default useV2Confirm;
